import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import logo from "../assets/IonTime.png";
import toplogo from "../assets/IonIdealogo.png";

export interface StatusHistoryItem {
  type: "approved" | "rejected" | "submitted";
  date: string;
  by: string;
  remark?: string;
  rejectedDates?: string;
}

type RGB = [number, number, number];

export interface WeekDayMeta {
  label: string;
  formattedDate: string;
  dayOfMonth: number;
  isWeekend: boolean;
  isHoliday: boolean;
  isRejected: boolean;
}

export interface TimesheetTableRow {
  project: string;
  description: string;
  days: string[];
}

export interface TimesheetPdfPayload {
  period: string;
  employeeName: string;
  approverName: string;
  tableData: TimesheetTableRow[];
  weekMeta: WeekDayMeta[];
  statusHistory?: StatusHistoryItem[];
}

type LegendKey = "weekend" | "holiday" | "rejected";

const COLORS: Record<LegendKey, { fill: RGB; text: RGB }> = {
  weekend: {
    fill: [238, 242, 255],
    text: [55, 48, 163],
  },
  holiday: {
    fill: [236, 253, 245],
    text: [4, 120, 87],
  },
  rejected: {
    fill: [254, 242, 242],
    text: [153, 27, 27],
  },
};

const LEGEND_ITEMS: { label: string; key: LegendKey }[] = [
  { label: "Week End", key: "weekend" },
  { label: "Public Holiday", key: "holiday" },
  { label: "Rejected Dates", key: "rejected" },
];

const getHighlightFill = (
  meta: WeekDayMeta | undefined,
  section: string
): RGB | null => {
  if (!meta) {
    return null;
  }

  const order: LegendKey[] =
    section === "body"
      ? ["rejected", "weekend", "holiday"]
      : ["weekend", "rejected", "holiday"];

  for (const key of order) {
    if (key === "rejected" && meta.isRejected) {
      return COLORS.rejected.fill;
    }
    if (key === "weekend" && meta.isWeekend) {
      return COLORS.weekend.fill;
    }
    if (key === "holiday" && meta.isHoliday) {
      return COLORS.holiday.fill;
    }
  }

  return null;
};

const normalizeTimeCell = (time?: string) => {
  const trimmed = (time ?? "").trim();
  if (!trimmed || trimmed === "0") {
    return "0:00";
  }
  return trimmed;
};

const parseTimeToHours = (time?: string) => {
  const normalized = normalizeTimeCell(time);
  const [hoursPart, minutesPart] = normalized.split(":");
  const hours = Number(hoursPart);
  const minutes = Number(minutesPart ?? "0");
  const safeHours = Number.isFinite(hours) ? hours : 0;
  const safeMinutes = Number.isFinite(minutes) ? minutes : 0;
  return safeHours + safeMinutes / 60;
};

const renderTimesheetPage = (doc: jsPDF, payload: TimesheetPdfPayload) => {
  const {
    period,
    employeeName,
    approverName,
    tableData,
    weekMeta,
    statusHistory = [],
  } = payload;

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  const logoWidth = 40;
  const logoHeight = 20;
  const leftPadding = 10;
  const spacingBetweenLogos = 10;

  doc.addImage(toplogo, "PNG", leftPadding, 10, logoWidth, logoHeight);
  doc.addImage(
    logo,
    "PNG",
    leftPadding + logoWidth + spacingBetweenLogos,
    10,
    logoWidth,
    logoHeight
  );

  doc.setTextColor(12, 23, 42);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text("IonIdea Interactive", pageWidth - 10, 20, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text(
    "#38-40, E.P.I.P, Whitefield, Bangalore-560066, India",
    pageWidth - 10,
    30,
    { align: "right" }
  );
  doc.text(
    "FAX +91-80-2841 1391, Phone +91-80-6658 1500",
    pageWidth - 10,
    40,
    { align: "right" }
  );

  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.text(`Timesheet Period: ${period}`, leftPadding, 55);
  doc.text(`Employee Name: ${employeeName}`, 250, 55);
  doc.text(`Approver: ${approverName}`, 450, 55);

  const normalizedRows = tableData.map((row) => ({
    ...row,
    days: weekMeta.map((_, idx) => normalizeTimeCell(row.days[idx])),
  }));

  const head = [
    [
      "Project - Activity",
      "Description",
      ...weekMeta.map((day) => `${day.label}`), //  ...weekMeta.map((day) => `${day.label}\n${day.formattedDate}`),
      "Total",
    ],
  ];

  let grandTotal = 0;
  const body = normalizedRows.map((row) => {
    const total = row.days.reduce(
      (acc, hours) => acc + parseTimeToHours(hours),
      0
    );
    grandTotal += total;
    return [row.project, row.description, ...row.days, total.toFixed(2)];
  });

  const totalRow = [
    {
      content: "Total Hours",
      colSpan: 2,
      styles: { halign: "center" as const, fontStyle: "bold" as const },
    },
    ...weekMeta.map((_, index) => {
      const dayTotal = normalizedRows.reduce(
        (acc, row) => acc + parseTimeToHours(row.days[index]),
        0
      );
      return dayTotal.toFixed(2);
    }),
    grandTotal.toFixed(2),
  ];

  autoTable(doc, {
    startY: 70,
    head,
    body,
    foot: [totalRow],
    theme: "grid",
    margin: { left: 10, right: 10 },
    styles: {
      fontSize: 7,
      cellPadding: 3,
      halign: "left",
    },
    headStyles: {
      fillColor: [240, 240, 240],
      textColor: [0, 0, 0],
      halign: "left",
    },
    footStyles: {
      fillColor: [230, 230, 230],
      textColor: [0, 0, 0],
      fontStyle: "bold",
      halign: "left",
    },
    columnStyles: {
      0: { cellWidth: 120 },
      1: { cellWidth: 160 },
    },
    didParseCell: (data) => {
      if (
        data.column.index >= 2 &&
        data.column.index < 2 + weekMeta.length
      ) {
        const dayMeta = weekMeta[data.column.index - 2];
        const fillColor = getHighlightFill(dayMeta, data.section);
        if (fillColor) {
          data.cell.styles.fillColor = fillColor;
          data.cell.styles.textColor = [0, 0, 0];
        }
      }
    },
    didDrawPage: () => {
      doc.setFontSize(6);
      doc.text(
        `Generated on ${new Date().toLocaleString()}`,
        pageWidth - 10,
        pageHeight - 10,
        { align: "left" }
      );
    },
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let currentY = (doc as any).lastAutoTable.finalY + 20;

  if (LEGEND_ITEMS.length > 0) {
    const chipHeight = 16;
    const chipPaddingX = 6;
    const horizontalGap = 12;
    let chipX = leftPadding;
    const chipY = currentY;

    doc.setFontSize(8);

    LEGEND_ITEMS.forEach((item) => {
      const textWidth = doc.getTextWidth(item.label);
      const chipWidth = textWidth + chipPaddingX * 2;
      const { fill, text } = COLORS[item.key];

      doc.setFillColor(...fill);
      doc.setDrawColor(255, 255, 255);
      doc.roundedRect(chipX, chipY, chipWidth, chipHeight, 6, 6, "F");

      doc.setTextColor(...text);
      doc.text(item.label, chipX + chipPaddingX, chipY + chipHeight / 2 + 3);

      chipX += chipWidth + horizontalGap;
    });

    doc.setTextColor(0, 0, 0);
    doc.setDrawColor(0, 0, 0);
    currentY = chipY + chipHeight + 12;
  }

  if (statusHistory.length > 0) {
    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.text("Status History", leftPadding, currentY);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);

    currentY += 10;
    statusHistory.forEach((item) => {
      let line = "";
      if (item.type === "approved") {
        line = `Approved on ${item.date} by ${item.by} - Remark: ${item.remark || "NA"}`;
      } else if (item.type === "rejected") {
        line = `Rejected on ${item.date} by ${item.by} - Reason: ${item.remark || "NA"} - Dates: ${item.rejectedDates || "-"}`;
      } else {
        line = `Submitted for approval on ${item.date} by ${item.by}`;
      }

      doc.text(line, leftPadding + 10, currentY);
      currentY += 12;
    });
  }
};

/**
 * Exports a single timesheet PDF.
 */
export const exportTimesheetPDF = (
  period: string,
  employeeName: string,
  approverName: string,
  tableData: TimesheetTableRow[],
  weekMeta: WeekDayMeta[],
  statusHistory: StatusHistoryItem[] = []
) => {
  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
  renderTimesheetPage(doc, {
    period,
    employeeName,
    approverName,
    tableData,
    weekMeta,
    statusHistory,
  });
  doc.save(`${employeeName}-Timesheet(${period}).pdf`);
};

/**
 * Exports multiple weekly payloads into a single PDF.
 */
export const exportTimesheetAllPDF = (
  payloads: TimesheetPdfPayload[],
  fileName?: string
) => {
  if (!payloads.length) {
    return;
  }

  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });

  payloads.forEach((payload, index) => {
    if (index > 0) {
      doc.addPage("a4", "landscape");
    }
    renderTimesheetPage(doc, payload);
  });

  const safeEmployee =
    payloads[0].employeeName?.trim().replace(/\s+/g, "-") || "Employee";

  let derivedRange = payloads[0].period.trim();
  if (payloads.length > 1) {
    const firstStart = payloads[0].period.split("-")[0]?.trim() ?? "";
    const lastEnd =
      payloads[payloads.length - 1].period.split("-").pop()?.trim() ?? "";
    derivedRange = `${firstStart} to ${lastEnd}`.trim();
  }

  const sanitizedRange = derivedRange
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-");

  const exportName =
    fileName ?? `${safeEmployee}-Timesheets(${sanitizedRange || "Combined"}).pdf`;

  doc.save(exportName);
};
